export type ChartFilters = Record<string, unknown[]> | null | undefined;

/**
 * An id that can never match a real location or admin group. Sent instead of
 * an empty `locations` array because some backends treat an empty filter
 * array as "no filter", which would silently widen a scoped request to the
 * whole account. An impossible id fails closed: worst case the request
 * returns no rows, never out-of-scope data.
 *
 * MUST stay a string: the reporting backend applies this filter to
 * ClickHouse's `Group` column (Text) without casting, and a numeric literal
 * there (`Group IN (-1)`) is a type error that 500s the report endpoints
 * (total-count, group-by-period, hourly-average, count-by-state). A string
 * compares cleanly and returns zero rows.
 */
export const NO_MATCH_LOCATION_ID = "-1";

// Merges the current breadcrumb scope's externalIds (locations + admin
// groups, normalized strings) into a report/chart `filters` object under the
// `locations` bucket key (the static filter-type id this app already uses for
// location filtering -- see src/actions/controls_actions.js's urlMap and
// src/reducers/app_defaults.js). If the caller already narrowed to specific
// ids, intersect rather than overwrite so a manual filter can only narrow
// further inside the scope, never escape it.
export const applyLocationScope = (
  filters: ChartFilters,
  applyScope: boolean,
  scopeExternalIds: Set<string>,
): ChartFilters => {
  if (!applyScope) return filters;

  // Defensive: popout-restored or otherwise deserialized props can carry a
  // plain object where a Set is expected. Treat it as an empty scope (fail
  // closed) instead of crashing on `scopeExternalIds.has`.
  const ids =
    scopeExternalIds instanceof Set ? scopeExternalIds : new Set<string>();

  const existing = filters?.locations;
  const scoped = Array.isArray(existing)
    ? existing.filter((id) => ids.has(String(id)))
    : Array.from(ids);

  return {
    ...(filters ?? {}),
    locations: scoped.length > 0 ? scoped : [NO_MATCH_LOCATION_ID],
  };
};
